import type { PageId, TravelChapter } from './types';

type ChapterPageId = Exclude<PageId, 'day2' | 'guide'>;

export const travelChapters: Record<ChapterPageId, TravelChapter> = {
  day1: {
    id: 'day1',
    marker: 'Day 1',
    title: '草原旅行的起点',
    subtitle: '从城市进入草原节奏',
    intro:
      '第一天不是空白页。它像一本书的地图页：先知道自己在哪里，风从哪里来，身体怎样慢下来，才看得懂后面六天。',
    routeStops: ['海拉尔集合', '历史博物馆', '呼伦贝尔古城', '草原天气', '迎宾礼', '旅行准备'],
    heroImage: '/assets/day1_route_map.svg',
    heroAlt: '本次呼伦贝尔旅行路线示意图',
    heroCredit: '本书制作：7 天路线地图',
    sections: [
      {
        id: 'where',
        eyebrow: '地图第一页',
        title: '呼伦贝尔不是一个小景区',
        deck: '它是一片很大的地理区域，有草原、森林、河流、湖泊和边境城市。',
        image: '/assets/day1_route_map.svg',
        imageAlt: '呼伦贝尔旅行路线示意图',
        imageCredit: '本书制作：从海拉尔出发的路线图',
        body: [
          '如果中国是一张大拼图，呼伦贝尔在东北方向，属于内蒙古自治区。这里靠近俄罗斯和蒙古国，所以你会在后面的旅程里看见边境河、口岸城市、不同语言的招牌和不一样的房子。',
          '海拉尔像这趟旅行的大门。我们从这里进入草原，再去额尔古纳、恩和、莫尔道嘎、黑山头、满洲里，最后回到海拉尔。先把路线放进脑子里，后面看到的风景就不会散成一片。'
        ],
        facts: ['位置：内蒙古东北部', '关键词：草原、森林、河流、湖泊、边境', '海拉尔是常见旅行入口'],
        lookFor: ['在地图上找到海拉尔、额尔古纳、满洲里。', '看路线有没有绕成一个大圈。', '想一想哪几天靠近河，哪几天靠近森林。'],
        whyItMatters: '孩子先建立空间感，后面的白桦林、驯鹿、界河和满洲里才不会只是一个个景点名称。',
        fieldQuestion: '如果你要给这趟旅行画第一条线，会先画路、河，还是边境？',
        resourceIds: ['cctv-morigele', 'xinhua-morigele'],
        callout: {
          title: '家长可以补充',
          text: '不要急着讲复杂行政区。先让孩子明白：呼伦贝尔很大，一次旅行会穿过几种完全不同的自然系统。'
        }
      },
      {
        id: 'history-museum',
        eyebrow: '历史博物馆',
        title: '先把时间放进地图里',
        deck: '博物馆不是“看一堆旧东西”，它把草原、森林、河流和人怎样生活过串成时间线。',
        image: '/assets/day1_history_museum_timeline.svg',
        imageAlt: '呼伦贝尔历史博物馆时间线示意图',
        imageCredit: '本书制作：博物馆时间线示意图',
        body: [
          '呼伦贝尔历史博物馆在海拉尔区。官方介绍里，它有史前、古代、近现代、非遗等展厅。也就是说，孩子可以先在一栋建筑里看见这片土地很长的时间。',
          '看展时不要急着记年代。先找三种线索：人用什么工具，动物和自然在哪里出现，不同民族怎样生活。等到了草原、白桦林和蒙古包，这些展柜里的线索会在真实地方重新出现。'
        ],
        facts: ['地点：海拉尔区满都拉街一带', '看点：地方历史、考古文物、非遗', '方法：先看时间线，再看生活物品'],
        lookFor: ['找一件和马、车、工具有关的文物。', '找展柜里出现的河流、湖泊、森林或草原。', '看说明牌里有没有蒙古族、达斡尔、鄂温克、鄂伦春等名字。'],
        whyItMatters: '先读时间，后面看到的风景就不会只是漂亮照片，而会变成有人生活过、移动过、选择过的地方。',
        fieldQuestion: '如果只能选一件文物讲给家人听，你会选哪一件，为什么？',
        resourceIds: ['hulunbuir-history-museum', 'hulunbuir-museum'],
        callout: {
          title: '家长可以补充',
          text: '不用把博物馆讲成历史课。让孩子自己选一个展柜、一件文物、一个问题，再把它和后面几天的自然景观连起来。'
        }
      },
      {
        id: 'ancient-city',
        eyebrow: '呼伦贝尔古城',
        title: '古城不是童话城堡，而是边地生活的影子',
        deck: '今天看到的古城街区，是在清代呼伦贝尔城原址一带恢复建设的历史街区。',
        image: '/assets/day1_ancient_city_gate.svg',
        imageAlt: '呼伦贝尔古城城门与街区示意图',
        imageCredit: '本书制作：古城街区观察示意图',
        body: [
          '呼伦贝尔古城在海拉尔区正阳街一带。历史上的呼伦贝尔城与清代边防、驻防和商贸有关；现在能逛到的是原址一带的复建街区，所以看它时要同时记住两件事：这里有历史线索，也有今天的城市生活。',
          '可以把古城当作“城市观察题”：城门为什么显眼？街道为什么适合人慢慢走？商号、衙门这些名字说明过去这里不只是住人的地方，也和管理、贸易、交通有关。'
        ],
        facts: ['位置：海拉尔区正阳街一带', '线索：清代边防与商贸', '现在：复建街区和城市休闲空间'],
        lookFor: ['找城门、街巷、商号或衙门名称。', '看哪些元素像历史，哪些明显属于今天。', '夜晚如果去，观察灯光怎样改变建筑感觉。'],
        whyItMatters: '孩子需要知道“古城”不一定等于完整保留下来的古建筑。学会区分原址、复建和今天的商业街区，本身就是很重要的历史观察。',
        fieldQuestion: '你看到的是过去留下来的东西，还是今天的人为了记住过去做出来的东西？',
        resourceIds: ['hulunbuir-name-origin'],
        callout: {
          title: '家长可以补充',
          text: '这里适合讲“历史线索”和“真实遗存”的区别。不要把复建街区讲成原封不动的古城，也不要因此否定它的观察价值。'
        }
      },
      {
        id: 'weather',
        eyebrow: '草原天气',
        title: '风会先告诉你草原有多大',
        deck: '开阔地没有很多高楼挡风，天气变化也更容易被身体感觉到。',
        image: '/assets/day2_grassland_river_overlook.jpg',
        imageAlt: '开阔草原与河流',
        imageCredit: '本书素材：开阔草原视角',
        body: [
          '在城市里，风常常被楼房切成一段一段；到了草原，风可以跑很远。你会觉得天空很低，云影移动很快，阳光和阴影在草地上换位置。',
          '第一天可以练习用身体读天气：风从哪边吹来？空气是干的还是湿的？云是薄薄一层，还是一大团？这些感觉会帮助你理解为什么草原旅行要带外套、防晒和水。'
        ],
        facts: ['开阔地风感更强', '早晚温差可能明显', '云影也能改变草原颜色'],
        lookFor: ['看草尖往哪边倒。', '观察云影在地面移动的速度。', '记下早晚穿衣有没有变化。'],
        whyItMatters: '天气不是背景，它会影响动物活动、人怎样穿衣、车辆怎样行驶，也会改变你看到的颜色。',
        fieldQuestion: '今天的风更像轻轻推你，还是一直追着你跑？',
        resourceIds: ['ibcas-grassland'],
        callout: {
          title: '一起想一想',
          text: '草原上为什么常常需要帽子和外套？这不是怕麻烦，而是因为太阳、风和温差会一起工作。'
        }
      },
      {
        id: 'welcome',
        eyebrow: '人文开场',
        title: '迎宾礼不是表演给游客看的动作',
        deck: '待客礼仪背后，是人们怎样欢迎远道而来的客人。',
        image: '/assets/day2_obao_hill.jpg',
        imageAlt: '草原高处敖包与彩旗',
        imageCredit: '本书素材：草原礼俗地标',
        body: [
          '在草原上，客人从很远的地方来，路上可能经历风、雨、长时间移动。欢迎客人、端上食物和奶茶，曾经是很重要的生活礼节。',
          '如果今天遇到迎宾礼，可以先看动作和物品：有没有哈达？有没有奶茶？人们站在哪里？声音是安静的、热闹的，还是像一首歌？看见礼仪，就是看见人与人之间怎样表达欢迎。'
        ],
        facts: ['礼仪表达欢迎', '物品常带有祝福含义', '体验时要保持尊重'],
        lookFor: ['观察人们先做什么，再做什么。', '听一听有没有歌声或祝福语。', '看自己需要怎样接过物品、怎样回应。'],
        whyItMatters: '人文内容不能只当作拍照项目。孩子需要知道，文化来自真实生活，也需要被认真对待。',
        fieldQuestion: '如果你去别人家做客，哪一个动作会让你感觉被欢迎？',
        resourceIds: ['obao-ich', 'mongolian-yurt'],
        callout: {
          title: '家长可以补充',
          text: '可以把“礼仪”讲成“人和人之间的安全感”。陌生人来到辽阔地方，被欢迎、被照顾，本身就是生活智慧。'
        }
      },
      {
        id: 'reader-kit',
        eyebrow: '使用这本书',
        title: '今天只练习一种本领：多看一分钟',
        deck: '不用把任务写满。能说出“我为什么这样看”，就已经开始成为观察者。',
        image: '/assets/day2_family_viewpoint.jpg',
        imageAlt: '一家人在高处观察草原',
        imageCredit: '本书素材：亲子观察视角',
        body: [
          '这本书不是考试卷。旅途中你不需要把每个空格都填上，也不需要背很多术语。你只要比平时多看一分钟，问一句为什么，就已经在读草原了。',
          '可以准备三样小工具：眼睛看远近，耳朵听风和动物，手指在地图上找路线。晚上再选一个最有意思的发现讲给家人听，这就是今天的旅行日记。'
        ],
        facts: ['先观察，再提问', '记录少一点也可以', '晚上复盘一个发现'],
        lookFor: ['找一个今天最明显的颜色。', '记住一个今天听到的声音。', '选一个你想问大人的问题。'],
        whyItMatters: '把观察任务压小，孩子更容易持续阅读；内容丰富，但使用方式要轻。',
        fieldQuestion: '今天你最想把哪一个发现带到 Day 2？',
        resourceIds: ['species-database'],
        callout: {
          title: '家长可以补充',
          text: '不要把孩子的问题立刻变成标准答案。先让他描述自己看到了什么，再一起查资料。'
        }
      }
    ]
  },
  day3: {
    id: 'day3',
    marker: 'Day 3',
    title: '白色树皮、马背和俄罗斯族村庄',
    subtitle: '从草原走到森林边缘',
    intro:
      '今天的关键词是“材料”。白桦树皮可以变成器物，马可以成为移动伙伴，木头可以变成适合寒冷地方的房子。',
    routeStops: ['额尔古纳', '白桦林', '非遗手作', '骑马体验', '恩和'],
    heroImage: '/assets/day3_birch_forest.jpg',
    heroAlt: '白桦林里的白色树干',
    heroCredit: 'AxelHH / Wikimedia Commons / CC BY 3.0',
    sections: [
      {
        id: 'birch-forest',
        eyebrow: '白桦林',
        title: '为什么白桦树像穿着白外套',
        deck: '白色树皮、黑色横纹和一层层剥落的表面，是认出白桦的关键线索。',
        image: '/assets/day3_birch_forest.jpg',
        imageAlt: '白桦林中的白色树干',
        imageCredit: 'AxelHH / Wikimedia Commons / CC BY 3.0',
        body: [
          '白桦最容易被认出来，因为树干常常是白色的，还带着黑色横纹。它的学名是 Betula platyphylla，喜欢光，也能适应寒冷地区，所以在北方森林和森林边缘很常见。',
          '白色树皮会一层层剥落，像很薄的纸。你不需要去撕它，只要近看那些横纹和卷起的小边，就能发现树皮不是一整块硬壳，而是会更新的保护层。'
        ],
        facts: ['学名：Betula platyphylla', '白色纸状树皮', '喜光、耐寒', '常见于北方林地和林缘'],
        lookFor: ['找黑色横纹最多的一棵树。', '比较年轻树和粗树的树皮颜色。', '观察地上有没有自然掉落的树皮碎片。'],
        whyItMatters: '认识白桦，是从“漂亮的白树”进入植物适应环境的第一步。',
        fieldQuestion: '白色树皮在阳光下更显眼，还是在阴天更显眼？',
        resourceIds: ['white-birch', 'species-database'],
        callout: {
          title: '家长可以补充',
          text: '如果孩子想撕树皮，可以改成找地上自然脱落的碎片。观察不等于破坏。'
        }
      },
      {
        id: 'birch-bark',
        eyebrow: '桦树皮手作',
        title: '一片树皮怎样变成生活用品',
        deck: '材料的故事，常常比成品更重要。',
        image: '/assets/day3_birch_bark.jpg',
        imageAlt: '白桦树皮纹理特写',
        imageCredit: 'Pöllö / Wikimedia Commons / CC BY 3.0',
        body: [
          '桦树皮轻、薄、有韧性，也比较耐水。在北方森林里，人们曾经用它做容器、包裹、装饰和手工艺品。它不是“随便捡来的皮”，而是森林给生活提供的材料。',
          '做手作时，可以先想材料为什么适合：它能弯吗？能卷吗？会不会太重？有没有天然纹理？当你这样想，就不只是做一个纪念品，而是在读森林生活。'
        ],
        facts: ['轻薄', '有韧性', '纹理清楚', '与北方森林生活有关'],
        lookFor: ['看手作品有没有保留树皮横纹。', '摸一摸材料是软、硬，还是有弹性。', '想想它适合装东西、装饰，还是遮盖。'],
        whyItMatters: '材料连接自然和文化。白桦不只是植物，也是很多生活经验的来源。',
        fieldQuestion: '如果只能用森林里的材料做一个小盒子，你会选树皮、木头，还是草？',
        resourceIds: ['white-birch', 'reindeer-ewenki'],
        callout: {
          title: '家长可以补充',
          text: '讲手作时，重点不是“买一个纪念品”，而是让孩子理解材料、环境和生活方式之间的关系。'
        }
      },
      {
        id: 'horseback',
        eyebrow: '马背体验',
        title: '骑马不是坐上一辆会走的车',
        deck: '马有身体、情绪、方向感和节奏。人需要和它合作。',
        image: '/assets/day2_mongolian_horse.jpg',
        imageAlt: '内蒙古草原上的马',
        imageCredit: 'Michael Mooney / Wikimedia Commons / CC BY 2.0',
        body: [
          '在草原生活里，马曾经帮助人移动、放牧、运输和传递消息。它不是只为游客存在的项目，而是草原生活的重要伙伴。',
          '骑马时要听从工作人员安排，观察马的耳朵、步伐和身体方向。真正厉害的不是“跑得快”，而是人能不能保持安静、平衡，并尊重马。'
        ],
        facts: ['马是草原移动伙伴', '耳朵和步态能传递状态', '安全比速度重要'],
        lookFor: ['看马耳朵朝向哪里。', '感受慢走和小跑节奏有什么不同。', '观察马群是不是喜欢靠近水草好的地方。'],
        whyItMatters: '把马从游乐项目还原为生活伙伴，孩子会更容易尊重动物。',
        fieldQuestion: '如果马忽然停下，它可能是在害怕、休息，还是听到了什么？',
        resourceIds: ['species-database', 'ibcas-grassland'],
        callout: {
          title: '一起想一想',
          text: '人骑在马上时，马也在感觉人。紧张、乱动和大喊，都会让合作变困难。'
        }
      },
      {
        id: 'enhe',
        eyebrow: '恩和',
        title: '木头房子为什么适合寒冷地方',
        deck: '村庄里的房子，也是在回答气候和材料的问题。',
        image: '/assets/day3_birch_forest.jpg',
        imageAlt: '北方森林中的树木',
        imageCredit: '本书素材：森林边缘生活背景',
        body: [
          '到了恩和，你会发现这里和草原营地很不一样：更多木头、更多院落、更多边境村庄的生活痕迹。木刻楞这类木屋，使用木材搭建，适合寒冷地区保温，也和当地森林资源有关。',
          '这里还可以看见俄罗斯族和华俄后裔生活留下的食物、建筑和语言线索。不要把它们当成“异国布景”，它们是边境地区长期交流形成的真实生活。'
        ],
        facts: ['木材来自森林环境', '木屋有保温需求', '边境村庄有多种文化线索'],
        lookFor: ['看房子外墙是不是木头横向排列。', '找一找院子、窗框和屋顶的形状。', '留意路牌、食物和语言有没有不同。'],
        whyItMatters: '建筑不是随便长出来的。材料、气候、边境交流，都会写进房子的样子里。',
        fieldQuestion: '如果这里没有大片森林，房子还会长得一样吗？',
        resourceIds: ['enhe-russian', 'white-birch'],
        callout: {
          title: '家长可以补充',
          text: '讲“异域风情”时要小心。更好的说法是：边境地区的人们长期生活、通婚、贸易和迁徙，形成了混合的地方风貌。'
        }
      }
    ]
  },
  day4: {
    id: 'day4',
    marker: 'Day 4',
    title: '走进大兴安岭，寻找驯鹿的森林',
    subtitle: '森林、苔藓和使鹿鄂温克',
    intro:
      '今天从村庄进入更深的森林。驯鹿不是普通“可爱动物”，它的蹄子、食物和生活方式，都在回答森林为什么重要。',
    routeStops: ['恩和', '越野车穿越', '驯鹿园', '森林小火车', '龙岩山', '莫尔道嘎'],
    heroImage: '/assets/day4_reindeer.jpg',
    heroAlt: '森林里的驯鹿',
    heroCredit: 'Glen Bowman / Wikimedia Commons / CC BY 2.0',
    sections: [
      {
        id: 'forest-edge',
        eyebrow: '森林草原过渡',
        title: '草原不是突然变成森林的',
        deck: '从开阔草地到密林，中间有一条慢慢变化的边缘地带。',
        image: '/assets/day4_taiga_forest.jpg',
        imageAlt: '北方针叶林和白桦混合森林',
        imageCredit: 'P199 / Wikimedia Commons / CC BY-SA 3.0',
        body: [
          '今天你会发现绿色变得不一样：草地减少，树变多，视线被树干分成一格一格。草原和森林不是用尺子切开的，中间常常有灌木、疏林和林缘。',
          '林缘很重要，因为它同时拥有开阔地和森林的特点。鸟可能在这里停歇，小动物可以躲进灌木，植物也会因为光照和水分不同而变化。'
        ],
        facts: ['林缘是过渡带', '光照、水分和土壤会改变植物', '动物常利用边缘环境'],
        lookFor: ['看树是不是越来越密。', '找草、灌木、树同时出现的地方。', '比较林内和林外的风声、温度和光。'],
        whyItMatters: '过渡带能帮助孩子理解生态不是黑白分明，而是连续变化。',
        fieldQuestion: '你是从哪一刻开始感觉“我们进森林了”？',
        resourceIds: ['white-birch', 'species-database'],
        callout: {
          title: '家长可以补充',
          text: '可以让孩子闭眼听十秒：草原上的风声和森林里的风声有什么不同？'
        }
      },
      {
        id: 'reindeer',
        eyebrow: '驯鹿',
        title: '驯鹿的脚，是写给森林和雪地的答案',
        deck: '宽蹄、角和食物偏好，说明它不是随便放在哪里都合适的动物。',
        image: '/assets/day4_reindeer.jpg',
        imageAlt: '驯鹿站在森林地面上',
        imageCredit: 'Glen Bowman / Wikimedia Commons / CC BY 2.0',
        body: [
          '驯鹿生活在寒冷地区，宽宽的蹄子能帮助它在松软地面或雪地上移动，也能拨开积雪寻找地衣等食物。它的角不是装饰，而是身体适应环境的一部分。',
          '在大兴安岭，讲驯鹿必须讲森林。地衣、苔藓、树木、雪地和人类照料，组成了驯鹿生活的系统。只说“摸驯鹿很可爱”，就把最重要的关系漏掉了。'
        ],
        facts: ['宽蹄适合松软地面', '常与寒冷森林环境相连', '地衣是重要食物线索'],
        lookFor: ['观察蹄子是不是比马蹄更宽。', '看驯鹿更喜欢走开阔地还是树荫下。', '找地面有没有苔藓、地衣或潮湿落叶。'],
        whyItMatters: '动物身体就是环境答案。看懂身体结构，孩子就能从“可爱”进入“适应”。',
        fieldQuestion: '如果把驯鹿放到又热又干的地方，它会遇到哪些困难？',
        resourceIds: ['reindeer-ewenki', 'species-database'],
        callout: {
          title: '家长可以补充',
          text: '不要把驯鹿讲成动物园动物。它和森林、使鹿鄂温克生活、现代保护与旅游转型都有关。'
        }
      },
      {
        id: 'moss-lichen',
        eyebrow: '地面小森林',
        title: '低头看，森林下面还有一层森林',
        deck: '苔藓和地衣很小，却能告诉你这里湿不湿、冷不冷、干扰多不多。',
        image: '/assets/day4_taiga_forest.jpg',
        imageAlt: '大兴安岭式森林地面',
        imageCredit: '本书素材：森林地表观察',
        body: [
          '森林不只有高高的树。地面上可能有苔藓、地衣、落叶、枯枝、蘑菇和小虫。它们把森林变成一个会储水、会分解、会重新长出生命的地方。',
          '苔藓像小海绵，地衣长得很慢。踩上去可能只是一脚，恢复却需要很久。所以在森林里走路，要尽量沿着道路和木栈道。'
        ],
        facts: ['苔藓能保持水分', '地衣生长慢', '落叶和枯枝会被分解'],
        lookFor: ['找树根旁边最湿润的一小块地。', '看看枯木上有没有新生命。', '比较路边和被踩踏地面的颜色。'],
        whyItMatters: '孩子会明白：森林的生命不只在头顶，也在脚下。',
        fieldQuestion: '为什么小小一块苔藓也值得保护？',
        resourceIds: ['species-database', 'reindeer-ewenki'],
        callout: {
          title: '一起想一想',
          text: '如果一条近路会踩过苔藓，另一条木栈道稍远一点，你会选哪条？'
        }
      },
      {
        id: 'forest-rail',
        eyebrow: '森林小火车',
        title: '一列小火车，藏着林区的历史',
        deck: '交通工具会告诉你，人们曾经怎样进入森林、运出木材、连接村镇。',
        image: '/assets/day4_taiga_forest.jpg',
        imageAlt: '森林中的高大树木',
        imageCredit: '本书素材：林区历史背景',
        body: [
          '森林小火车听起来像童话，但它也和林区历史有关。过去人们需要道路、铁轨和车辆，把木材、生活物资和人连接起来。',
          '今天坐小火车，可以不只看窗外风景，还要想：轨道为什么要沿着地形走？森林开发之后，怎样修复和保护？一段交通史，也能变成一堂生态课。'
        ],
        facts: ['林区交通连接资源和生活', '轨道要顺应地形', '现代旅行应关注保护和修复'],
        lookFor: ['看轨道两边树木密度是否不同。', '观察火车转弯时地形有什么变化。', '找一处能看出“人修过”的痕迹。'],
        whyItMatters: '孩子能看到人类活动和森林保护之间的张力，不把森林只当作拍照背景。',
        fieldQuestion: '如果你是森林管理员，会把道路修得越多越好，还是尽量少一点？',
        resourceIds: ['reindeer-ewenki', 'species-database'],
        callout: {
          title: '家长可以补充',
          text: '讲历史时避免简单说“开发不好”。更准确的是：人依靠资源生活，也需要在使用后学习修复和保护。'
        }
      }
    ]
  },
  day5: {
    id: 'day5',
    marker: 'Day 5',
    title: '边境河、坡地和蒙古包',
    subtitle: '地理怎样决定生活',
    intro:
      '今天把三个问题放在一起：河为什么能成为边境？坡为什么让人滑得快？蒙古包为什么适合草原？',
    routeStops: ['莫尔道嘎', '太平', '室韦界河', '乌兰山', '云朵牧场', '蒙古包'],
    heroImage: '/assets/day5_yurt_grassland.jpg',
    heroAlt: '草原上的蒙古包',
    heroCredit: 'Bmdavll / Wikimedia Commons / CC BY-SA 4.0',
    sections: [
      {
        id: 'border-river',
        eyebrow: '室韦界河',
        title: '国界有时不是墙，而是一条河',
        deck: '额尔古纳河让孩子看见：地图上的线，也可能是会流动的水。',
        image: '/assets/day2_morigele_meander.jpg',
        imageAlt: '弯曲河流俯瞰',
        imageCredit: '本书素材：河流与边界想象',
        body: [
          '在地图上，边境常常是一条细线；到了现场，你可能看到的是一条河、对岸的村庄、边防设施和来往的船。界河让抽象的国家边界变成可以看见的地理。',
          '河会流动，也会带来交流。边境地区的人们可能有相似的食物、亲缘、贸易和语言线索。理解边境，不只是知道“这边那边”，也要知道人们怎样在边界附近生活。'
        ],
        facts: ['界河可以作为边界', '河流也连接交通和交流', '边境生活有多种文化线索'],
        lookFor: ['看对岸和这岸有什么相似或不同。', '找边界提醒标识。', '观察河岸是陡、缓，还是有浅滩。'],
        whyItMatters: '孩子会明白边境不是神秘禁区，而是地理、历史和生活共同形成的空间。',
        fieldQuestion: '一条河为什么既能分开两边，也能连接两边？',
        resourceIds: ['cng-river', 'ergun-city'],
        callout: {
          title: '家长可以补充',
          text: '如果现场有边防提示，先讲规则和安全，再讲地理。尊重边境管理也是旅行学习的一部分。'
        }
      },
      {
        id: 'slope',
        eyebrow: '乌兰山',
        title: '滑草是在和坡度、重力、摩擦力玩游戏',
        deck: '好玩的背后也有科学：坡越陡，向下的力量越明显。',
        image: '/assets/day2_grassland_river_overlook.jpg',
        imageAlt: '草原坡地和河流',
        imageCredit: '本书素材：草原坡地',
        body: [
          '滑草时，你会感觉身体被往下拉。这不是魔法，而是重力在斜坡上的作用。坡越陡，向下滑的感觉越明显；草地和滑具之间的摩擦，会让速度变慢。',
          '同一座坡，从高处看像大地的褶皱；坐上滑具，又变成速度和方向的体验。一个地方可以同时是地貌、游戏场和观察课。'
        ],
        facts: ['重力让物体向下运动', '坡度影响速度', '摩擦力会减慢滑行'],
        lookFor: ['比较陡坡和缓坡速度差异。', '看草地干湿会不会影响滑行。', '观察安全员选择的路线是不是避开坑洼。'],
        whyItMatters: '孩子能把身体体验和基础科学连起来，而不只是说“好玩”。',
        fieldQuestion: '为什么同样是下坡，有的地方滑得快，有的地方滑得慢？',
        resourceIds: ['ibcas-grassland'],
        callout: {
          title: '一起想一想',
          text: '游玩前先看安全边界：哪里能滑，哪里不能滑，本身也是读地形。'
        }
      },
      {
        id: 'yurt',
        eyebrow: '蒙古包',
        title: '圆圆的家，为什么适合草原',
        deck: '蒙古包不是“造型特别”，而是在回答风、移动和生活空间的问题。',
        image: '/assets/day5_yurt_grassland.jpg',
        imageAlt: '草原上的蒙古包',
        imageCredit: 'Bmdavll / Wikimedia Commons / CC BY-SA 4.0',
        body: [
          '蒙古包的圆形结构，能让风绕过去；骨架和围毡可以拆装，适合过去逐水草而居的牧业生活。它看起来简单，其实是很多代人试出来的智慧。',
          '走进蒙古包，可以看天窗、炉子、门的方向、座位安排和物品摆放。不要只拍外形，试着看一看：这个家怎样保暖、通风、待客和移动。'
        ],
        facts: ['圆形减少迎风面', '结构便于拆装', '内部空间有生活秩序'],
        lookFor: ['看顶部有没有天窗。', '观察门口朝向和内部中心位置。', '找骨架、围毡、绳带怎样连接。'],
        whyItMatters: '建筑是环境和生活方式的答案。蒙古包让孩子看到“家”可以不是固定不动的房子。',
        fieldQuestion: '如果风很大，方房子和圆房子哪个更容易被风正面撞上？',
        resourceIds: ['mongolian-yurt', 'obao-ich'],
        callout: {
          title: '家长可以补充',
          text: '讲蒙古包时不要停在“特色建筑”。要把它和草原风、牧业移动、家庭空间、待客礼仪连起来。'
        }
      },
      {
        id: 'milk-tea',
        eyebrow: '奶茶和羊毛毡',
        title: '生活里的智慧，常常藏在食物和材料里',
        deck: '咸奶茶、奶制品、羊毛毡，都在回答草原上怎样吃、怎样保暖、怎样待客。',
        image: '/assets/day5_milk_tea.jpg',
        imageAlt: '蒙古奶茶和奶食',
        imageCredit: 'Yakov Fedorov / Wikimedia Commons / CC BY-SA 2.0',
        body: [
          '草原生活里，牛羊马提供的不只是交通和景观，也提供奶、肉、毛和皮。奶茶能补充热量和水分，奶制品便于保存，羊毛毡能保暖，也能成为蒙古包的重要材料。',
          '尝一口奶茶时，可以问：为什么它可能是咸的？为什么茶、奶、盐会放在一起？食物不是单纯的味道，也是一片土地给人的生活办法。'
        ],
        facts: ['奶茶与待客礼仪相关', '羊毛可以做毡', '食物和材料来自牧业生活'],
        lookFor: ['奶茶里有没有炒米或奶皮。', '摸一摸毡制品是厚、薄，还是紧实。', '看餐桌上哪些食物来自牛羊奶。'],
        whyItMatters: '孩子能从味觉进入生活史，知道食物不是“奇怪味道”，而是环境里的选择。',
        fieldQuestion: '如果冬天很冷，什么材料最适合让家里暖一点？',
        resourceIds: ['mongolian-yurt', 'ibcas-grassland'],
        callout: {
          title: '家长可以补充',
          text: '孩子如果不喜欢咸奶茶，可以先描述味道，不要嘲笑。不同食物背后是不同环境和生活经验。'
        }
      }
    ]
  },
  day6: {
    id: 'day6',
    marker: 'Day 6',
    title: '草原电影感与边境童话城',
    subtitle: '从自然秘境进入满洲里',
    intro:
      '今天从开阔草原走向边境城市。你会发现：城市也能像一本地理书，写着铁路、口岸、贸易和文化符号。',
    routeStops: ['黑山头', '呼籁秘境', '套娃景区', '大马戏', '满洲里'],
    heroImage: '/assets/day6_manzhouli.jpg',
    heroAlt: '满洲里城市建筑夜景',
    heroCredit: 'Yaoleilei / Wikimedia Commons / CC BY-SA 4.0',
    sections: [
      {
        id: 'cinematic-grassland',
        eyebrow: '呼籁秘境',
        title: '为什么草原很像一场大电影',
        deck: '镜头感来自天空、地平线、云影、风和人的尺度。',
        image: '/assets/day2_hero_morigele_overlook.jpg',
        imageAlt: '高处俯瞰草原河流',
        imageCredit: '本书素材：草原高处视角',
        body: [
          '草原让人觉得“电影感强”，不是因为它会表演，而是因为画面里有很大的天空、很远的地平线、移动的云影和很小的人。尺度差异越大，画面越有空间感。',
          '如果有无人机或高处视角，你会看到地面上看不见的线：车道、河道、草色变化、营地位置。地面视角看细节，高处视角看关系。'
        ],
        facts: ['地平线增强空间感', '高处能看见关系', '云影会改变画面层次'],
        lookFor: ['找画面里最远的一条线。', '比较地面拍照和高处俯瞰的区别。', '看人的大小如何让草原显得更辽阔。'],
        whyItMatters: '孩子会知道“好看”也可以被分析：构图、尺度和视角都会影响感受。',
        fieldQuestion: '如果你是摄影师，今天会把人拍得很大，还是很小？为什么？',
        resourceIds: ['cctv-photo', 'xinhua-morigele'],
        callout: {
          title: '家长可以补充',
          text: '可以让孩子选一张照片，说出里面的三条线：地平线、道路线、水线或人移动的线。'
        }
      },
      {
        id: 'manzhouli',
        eyebrow: '满洲里',
        title: '边境城市为什么不像普通城市',
        deck: '口岸、铁路、贸易和邻国文化，会改变一座城市的样子。',
        image: '/assets/day6_manzhouli.jpg',
        imageAlt: '满洲里城市建筑',
        imageCredit: 'Yaoleilei / Wikimedia Commons / CC BY-SA 4.0',
        body: [
          '满洲里靠近俄罗斯和蒙古国，是重要边境口岸城市。货物、车辆、火车和旅客在这里进出，城市自然会留下贸易和交通的痕迹。',
          '你可能会看见俄式建筑符号、双语招牌、套娃形象、口岸设施和铁路相关元素。这些不是随意装饰，而是在提醒你：边境城市长期和外部世界相连。'
        ],
        facts: ['边境口岸城市', '铁路和贸易影响城市', '多种文化符号并存'],
        lookFor: ['找一块有多种文字的招牌。', '观察建筑屋顶和颜色有什么不同。', '留意哪里能看见铁路或口岸元素。'],
        whyItMatters: '城市也有地理成因。孩子能把满洲里的“童话感”连接到真实的边境和贸易。',
        fieldQuestion: '如果一座城市每天都有很多跨境货物经过，它会出现哪些特别设施？',
        resourceIds: ['manzhouli-city'],
        callout: {
          title: '家长可以补充',
          text: '讲满洲里时不要只讲“漂亮”。重点是口岸、铁路、边境交流让城市形成了独特风貌。'
        }
      },
      {
        id: 'matryoshka',
        eyebrow: '套娃',
        title: '一个娃娃，为什么能代表一种文化符号',
        deck: '套娃既是俄罗斯民间工艺符号，也被边境旅游重新放大。',
        image: '/assets/day6_manzhouli.jpg',
        imageAlt: '满洲里城市与俄罗斯风格建筑',
        imageCredit: '本书素材：边境城市符号',
        body: [
          '套娃是一层套一层的木制玩具，常被当作俄罗斯文化符号。到了满洲里，它又变成景区、建筑和城市记忆的一部分。',
          '看套娃时，可以问两个问题：它原来是什么？在这里又被怎样使用？很多旅游符号都经历了这样的变化，从生活物品或民间工艺，变成城市名片。'
        ],
        facts: ['文化符号会被重新使用', '旅游会放大某些形象', '符号不等于全部文化'],
        lookFor: ['套娃上有哪些颜色和图案。', '看它出现在玩具、建筑、路牌还是商店里。', '想一想它为什么适合被做成城市标志。'],
        whyItMatters: '孩子能学会不把一个符号当成全部文化，而是理解符号怎样被选择和传播。',
        fieldQuestion: '如果让你选一个物品代表这趟草原旅行，你会选马、河、白桦，还是湖？',
        resourceIds: ['manzhouli-city'],
        callout: {
          title: '一起想一想',
          text: '套娃很有趣，但俄罗斯文化不只有套娃。就像草原文化也不只有马和蒙古包。'
        }
      },
      {
        id: 'circus-physics',
        eyebrow: '大马戏',
        title: '表演里的平衡、力量和合作',
        deck: '看表演不只是看热闹，也可以看身体怎样控制重心和节奏。',
        image: '/assets/day6_manzhouli.jpg',
        imageAlt: '满洲里夜景与演艺城市氛围',
        imageCredit: '本书素材：城市夜景',
        body: [
          '如果今天看大马戏，可以把它当成一堂身体科学课。杂技演员需要控制重心，骑手需要和马保持节奏，团队动作需要精确配合。',
          '观看时也要记得：台上一分钟，台下可能是很长时间的练习。真正值得观察的，不只是“惊险”，还有安全、信任、训练和合作。'
        ],
        facts: ['重心影响平衡', '节奏影响配合', '长期训练保证安全'],
        lookFor: ['看演员怎样保持身体稳定。', '听音乐节奏和动作节奏是否一致。', '观察团队动作有没有互相等待和保护。'],
        whyItMatters: '孩子能把表演从刺激体验扩展到物理、身体控制和团队合作。',
        fieldQuestion: '一个人保持平衡时，眼睛、手臂和脚分别在帮什么忙？',
        resourceIds: ['species-database'],
        callout: {
          title: '家长可以补充',
          text: '如果表演涉及动物，可以引导孩子观察训练与照护，不把动物只当成道具。'
        }
      }
    ]
  },
  day7: {
    id: 'day7',
    marker: 'Day 7',
    title: '马背上的告别，湖边的最后一课',
    subtitle: '呼和诺尔湖与旅行回望',
    intro:
      '最后一天不是结束，而是把前面看见的河、草、马、鸟、城市和人重新连成一张自己的地图。',
    routeStops: ['满洲里', '巴尔虎马之舞', '呼和诺尔湖', '海拉尔送机', '整理记忆'],
    heroImage: '/assets/day7_hulun_lake.jpg',
    heroAlt: '草原湖泊与湿地参考照片',
    heroCredit: 'Mizu basyo / Wikimedia Commons / CC BY-SA 3.0；草原湖泊参考照片，非呼和诺尔实景',
    sections: [
      {
        id: 'horse-dance',
        eyebrow: '马之舞',
        title: '马为什么是草原上的超级伙伴',
        deck: '速度、耐力、方向感和人与动物的合作，让马成为草原文化里的重要角色。',
        image: '/assets/day2_mongolian_horse.jpg',
        imageAlt: '草原上的马',
        imageCredit: 'Michael Mooney / Wikimedia Commons / CC BY 2.0',
        body: [
          '经过几天旅行，你已经不只把马看成可以骑的动物。马能帮助移动、放牧、运输，也进入音乐、节庆、竞技和故事里。',
          '看马之舞时，可以观察马和人的配合：什么时候加速，什么时候转弯，什么时候停下。真正的重点不是“人控制马”，而是长期训练后的合作。'
        ],
        facts: ['马与移动生活相关', '节奏和方向需要配合', '马文化来自真实生活经验'],
        lookFor: ['看马转弯时身体怎样倾斜。', '听音乐节奏和马步是否一致。', '观察骑手手、腿和身体重心的变化。'],
        whyItMatters: '这能把 Day3 的骑马体验和草原文化主线收束起来。',
        fieldQuestion: '如果没有马，草原上的移动和放牧会怎样改变？',
        resourceIds: ['mongolian-yurt', 'species-database'],
        callout: {
          title: '家长可以补充',
          text: '不要只问“好不好看”。可以问：你觉得马和人怎样互相信任？'
        }
      },
      {
        id: 'lake-grassland',
        eyebrow: '呼和诺尔湖',
        title: '湖泊会让草原变得不一样',
        deck: '有水的地方，植物、鸟、昆虫和人的活动都会发生变化。',
        image: '/assets/day7_hulun_lake.jpg',
        imageAlt: '草原湖泊与湿地参考照片',
        imageCredit: 'Mizu basyo / Wikimedia Commons / CC BY-SA 3.0；草原湖泊参考照片，非呼和诺尔实景',
        body: [
          '今天看的是呼和诺尔草原一带的湖泊和湿地景观。它不是呼伦湖，但和呼伦贝尔许多草原湖泊一样，有水的地方会让周围植物、鸟类、昆虫和人的活动发生变化。',
          '站在湖边看，不要只看水面蓝不蓝。看看岸边有没有浅滩、水草、鸟、虫、脚印和风吹出的波纹。湖泊是草原里的另一个生态中心。'
        ],
        facts: ['湖泊改变水分条件', '岸边常有更多生命线索', '浅滩和水草对水鸟重要'],
        lookFor: ['找湖岸从水到草的过渡带。', '看鸟是在水面、浅滩还是天空。', '观察风向怎样改变波纹。'],
        whyItMatters: '最后一天用湖泊把湿地、鸟类、草原水分这些线索再次连接起来。',
        fieldQuestion: '为什么湖边常常比远处草地更容易看到鸟？',
        resourceIds: ['chenbarag-lakes', 'nfga-wetland', 'migration-plan'],
        callout: {
          title: '家长可以补充',
          text: '可以把湖讲成“草原上的补给站”：给动物水、给鸟停歇、给植物不同的生长条件。'
        }
      },
      {
        id: 'waterbirds',
        eyebrow: '候鸟',
        title: '水鸟为什么需要一站一站休息',
        deck: '迁徙不是一直飞到底。湖泊和湿地像路上的服务区。',
        image: '/assets/day2_whooper_swan.jpg',
        imageAlt: '大天鹅水鸟',
        imageCredit: 'Estormiz / Wikimedia Commons / CC0',
        body: [
          '很多鸟会随季节迁徙，路上需要停下来休息、取食、补充体力。湖泊、湿地和浅水区，就像迁徙路线上的一站一站。',
          '我们不一定能准确叫出每一种鸟的名字，但可以先观察它的行为：游水、涉水、低飞、成群、鸣叫。行为比名字更容易在现场读出来。'
        ],
        facts: ['迁徙需要停歇地', '湖泊湿地提供食物和安全', '先观察行为，再识别物种'],
        lookFor: ['看鸟是单独活动还是成群。', '观察它的脚是在水里走，还是在水面游。', '注意不要靠太近惊扰。'],
        whyItMatters: '这让孩子知道保护一个湖，不只是保护水，也是在保护一条迁徙道路。',
        fieldQuestion: '如果迁徙路上的湿地越来越少，鸟会遇到什么困难？',
        resourceIds: ['migration-plan', 'nfga-wetland', 'species-database'],
        callout: {
          title: '一起想一想',
          text: '看鸟最好的距离，是鸟还能安心做自己的事情。'
        }
      },
      {
        id: 'memory-map',
        eyebrow: '旅行回望',
        title: '把七天变成一张自己的地图',
        deck: '最好的纪念品，是你能讲出这片土地怎样连在一起。',
        image: '/assets/day1_route_map.svg',
        imageAlt: '7 天呼伦贝尔路线地图',
        imageCredit: '本书制作：旅行回望地图',
        body: [
          '现在回头看，你见过弯弯的河、白色的树、森林里的驯鹿、边境河、蒙古包、满洲里和湖边水鸟。它们不是分开的景点，而是一张大地图上的不同答案。',
          '可以给这趟旅行选四个词：一个颜色、一个声音、一个动物、一个问题。把它们写下来，下一次看到草原、森林或湖泊，你就会比以前多看懂一点。'
        ],
        facts: ['颜色：草、河、湖、森林', '声音：风、马、鸟、火车', '问题：为什么这里长这样'],
        lookFor: ['选一张最能代表七天的照片。', '画出你记住的三条线：河、路、边境或湖岸。', '写下一个回家后还想查的问题。'],
        whyItMatters: '旅行结束后复盘，能把零散体验变成长期记忆和继续学习的入口。',
        fieldQuestion: '如果给未来的自己留一句话，你会提醒他再看什么？',
        resourceIds: ['cng-river', 'white-birch', 'reindeer-ewenki', 'chenbarag-lakes'],
        callout: {
          title: '家长可以补充',
          text: '最后不要做成总结考试。让孩子自己选一个最想保留的发现，比逐项复述更有效。'
        }
      }
    ]
  }
};
